// Creates an object URL for a blob, triggers a browser download via a transient
// anchor tag, then revokes the URL. An existing copy of this dance lives in
// UploadCard/utils/api/helpers.ts (downloadFile) — left as-is (out of scope); this
// is the single implementation for new callers, and that can migrate later.
export function downloadBlobFromUrl(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/** Extracts a filename from a `Content-Disposition` response header (RFC 6266) — prefers the
 * `filename*=UTF-8''...` extended form, then falls back to a quoted or bare `filename=`.
 * Returns null when the header is missing or carries no filename token, so callers can fall
 * back to a fixed safe literal instead of rebuilding a name client-side. */
export function extractFilenameFromContentDisposition(contentDisposition: string | null | undefined): string | null {
    if (!contentDisposition) return null;

    // RFC 8187's language field is optional but permitted, so `filename*=UTF-8'en'Consent.pdf`
    // is valid and must not fall through to the fixed literal.
    const extended = /filename\*=UTF-8'[^']*'([^;]+)/i.exec(contentDisposition);
    if (extended?.[1]) {
        try {
            const decoded = decodeURIComponent(extended[1].trim());
            // aica never emits this extended form today — the plain `filename="..."` branch below
            // is the only one it sends, built server-side from a sanitised org field. This branch
            // only exists to parse a header shape a proxy could rewrite, so treat the decoded value
            // as untrusted: reject path separators, null bytes, and other control characters rather
            // than handing them to a download, and fall back to the caller's fixed literal instead.
            if (/[/\\\0-\x1f\x7f]/.test(decoded)) return null;
            return decoded;
        } catch {
            return null;
        }
    }

    const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
    if (quoted?.[1]) return quoted[1];

    const bare = /filename=([^;]+)/i.exec(contentDisposition);
    return bare?.[1]?.trim() || null;
}
